import { useEffect, useState } from "react";

import { useRefresh } from "@smart/itops-hooks-dom";
import { Table } from "@smart/itops-sb-design-system-dom";
import { useLoadPendingSubmissions } from "@smart/manage-gql-client-dom";

import { BulkCloseFormModal } from "./bulk-close-form-modal";
import { BulkDeleteFormModal } from "./bulk-delete-form-modal";
import { BulkSendReminderModal } from "./bulk-send-reminder-modal";
import { Empty } from "./empty";
import { SubmissionActionsWithoutMatterContext } from "../../shared/submission-actions/actions-without-matter-context";
import { GqlSubmissionSummary } from "../../types";
import { useFilterSync } from "../filter-sync-context";
import {
  extendSubmissions,
  usePendingSubmissionsColumns,
  useSubmissionsWithMatter,
  useSupplementalMatterTypes,
} from "../hooks";

export const PendingSubmissions = ({
  pendingSubmissions,
}: {
  pendingSubmissions: ReturnType<typeof useLoadPendingSubmissions>;
}) => {
  const [tableKey, setTableKey] = useState(0);
  const filterSync = useFilterSync();
  const {
    columns,
    select,
    sort,
    onRowClick,
    cancelling,
    deleting,
    reminding,
    setTimeFilter,
  } = usePendingSubmissionsColumns();
  const [submissionsToSend, setSubmissionsToSend] = useState<
    GqlSubmissionSummary[] | null
  >(null);

  const [submissionsToDelete, setSubmissionsToDelete] = useState<
    GqlSubmissionSummary[] | null
  >(null);

  const [submissionsToClose, setSubmissionsToClose] = useState<
    GqlSubmissionSummary[] | null
  >(null);

  const [key, refresh] = useRefresh();

  const { loading, result, refetching, refetch } = pendingSubmissions;
  const { submissionsWithMatter: pendingSubmissionsWithMatter } =
    useSubmissionsWithMatter({
      submissions: result,
      loading,
    });
  const supplementalMatterTypes = useSupplementalMatterTypes(
    pendingSubmissionsWithMatter,
  );

  // Filter those cancelled or deleted after those actions at the frontend
  // where those submissions are still in cache
  const filteredSubmissions = pendingSubmissionsWithMatter.filter(
    (s) => s.values.status !== "cancelled" && !s.values.deleted,
  );

  const onDone = () => {
    refresh();
    refetch();
  };

  useEffect(() => {
    if (filterSync?.tabKey !== "pending-forms") return;

    setTimeFilter(filterSync.period, filterSync.range);
    setTableKey(filterSync.nonce);
  }, [filterSync, setTimeFilter]);

  return (
    <>
      <Table
        key={`intake-pending-submissions-${key}-${tableKey}`}
        id="intake-pending-submissions"
        data={extendSubmissions(filteredSubmissions, supplementalMatterTypes)}
        columns={columns}
        updating={loading || refetching}
        loadingRows={loading ? 3 : undefined}
        getRowId={(row) => row.uri}
        select={select}
        sort={sort}
        globalFilter={undefined}
        empty={<Empty message="No pending forms found" />}
        multiSelect
        batchActions={[
          {
            text: "Send Reminders",
            onClick: (selected) => setSubmissionsToSend(selected),
          },
          {
            text: "Close Forms",
            onClick: (selected) => setSubmissionsToClose(selected),
          },
          {
            text: "Delete Forms",
            onClick: (selected) => setSubmissionsToDelete(selected),
            className: "text-red-550",
          },
        ]}
        onRowClick={onRowClick}
        enableColumnFilters
        className="rounded-b-none"
        fixedHeadings
      />
      {submissionsToSend?.length && (
        <BulkSendReminderModal
          submissions={submissionsToSend}
          onClose={() => setSubmissionsToSend(null)}
        />
      )}
      {submissionsToDelete?.length && (
        <BulkDeleteFormModal
          submissions={submissionsToDelete}
          onClose={() => setSubmissionsToDelete(null)}
          onDone={onDone}
        />
      )}
      {submissionsToClose?.length && (
        <BulkCloseFormModal
          submissions={submissionsToClose}
          onClose={() => setSubmissionsToClose(null)}
          onDone={onDone}
        />
      )}
      <SubmissionActionsWithoutMatterContext
        cancelling={cancelling}
        deleting={deleting}
        reminding={reminding}
        refetch={refetch}
      />
    </>
  );
};
